import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  Dropdown,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { Database, MoreHorizontal, Play, RefreshCw, RotateCcw, Sigma, Upload } from 'lucide-react';
import { avmApi } from '../../services/api';
import AvmSalesTab from './AvmSalesTab';
import AvmFotocasaListingsTab from './AvmFotocasaListingsTab';
import AvmIdealistaListingsTab from './AvmIdealistaListingsTab';
import AvmCalculationsTab from './AvmCalculationsTab';
import AvmSessionsTab from './AvmSessionsTab';
import AvmValoradorEmailsTab from './AvmValoradorEmailsTab';

const { Title, Text } = Typography;

const getApiErrorMessage = (error: any, fallback: string) => {
  const payload = error?.response?.data;
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (Array.isArray(payload?.message)) return payload.message.join(' | ');
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  return fallback;
};

interface AvmZone {
  zoneId: string;
  island: string;
  municipality?: string;
  zone?: string;
  neighborhood: string;
  latitudeCenter?: number;
  longitudeCenter?: number;
  microzoneClassification?: string;
  mediaIdealista?: number;
  mediaFotocasa?: number;
  mediaEngelVolkers?: number;
  mediaPortalesTotal?: number;
  mediaVentasNotaria?: number;
  mediaVentasSwomey?: number;
  mediaVentasRealesTotal?: number;
  coefAntiguedadVentas?: number;
  coefHistorico?: number;
  precioM2Final?: number;
  motorUsed?: 1 | 2 | null;
}

const formatNumber = (value?: number) => (typeof value === 'number' ? value.toLocaleString('es-ES') : '-');

export default function AvmPage() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [rows, setRows] = useState<AvmZone[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runIdealistaModalOpen, setRunIdealistaModalOpen] = useState(false);
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [savingFotocasaFor, setSavingFotocasaFor] = useState<string | null>(null);
  const [runningApify, setRunningApify] = useState(false);
  const [runningIdealista, setRunningIdealista] = useState(false);
  const [reprocessingDataset, setReprocessingDataset] = useState(false);
  const [recomputingSales, setRecomputingSales] = useState(false);
  const [recomputingFinalPrices, setRecomputingFinalPrices] = useState(false);
  const [fotocasaForm] = Form.useForm();
  const [runForm] = Form.useForm();
  const [runIdealistaForm] = Form.useForm();
  const [reprocessForm] = Form.useForm();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await avmApi.listZones({ search: query, limit: 500 });
      setRows(data.data || []);
    } catch {
      message.error('No se pudo cargar el listado AVM');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const columns = useMemo(
    () => [
      {
        title: 'ID_ZONA',
        dataIndex: 'zoneId',
        key: 'zoneId',
        fixed: 'left' as const,
        width: 120,
        sorter: (a: AvmZone, b: AvmZone) =>
          a.zoneId.localeCompare(b.zoneId, 'es', { numeric: true }),
        defaultSortOrder: 'ascend' as const,
      },
      { title: 'ISLA', dataIndex: 'island', key: 'island', width: 120 },
      { title: 'MUNICIPIO', dataIndex: 'municipality', key: 'municipality', width: 150 },
      { title: 'ZONA', dataIndex: 'zone', key: 'zone', width: 160 },
      { title: 'BARRIO', dataIndex: 'neighborhood', key: 'neighborhood', width: 220 },
      {
        title: 'CLASIFICACION_MICROZONA',
        dataIndex: 'microzoneClassification',
        key: 'microzoneClassification',
        width: 190,
        render: (v: string) => {
          if (!v) return '-';
          const color = v === 'Premium' ? 'gold' : v === 'Alto' ? 'green' : v === 'Medio' ? 'blue' : 'default';
          return <Tag color={color}>{v}</Tag>;
        },
      },
      {
        title: 'LATITUD_CENTRO',
        dataIndex: 'latitudeCenter',
        key: 'latitudeCenter',
        width: 140,
        render: (v: number) => formatNumber(v),
      },
      {
        title: 'LONGITUD_CENTRO',
        dataIndex: 'longitudeCenter',
        key: 'longitudeCenter',
        width: 150,
        render: (v: number) => formatNumber(v),
      },
      { title: 'media_idealista', dataIndex: 'mediaIdealista', key: 'mediaIdealista', width: 140, render: (v: number) => formatNumber(v) },
      { title: 'media_fotocasa', dataIndex: 'mediaFotocasa', key: 'mediaFotocasa', width: 140, render: (v: number) => formatNumber(v) },
      { title: 'media_engel_volkers', dataIndex: 'mediaEngelVolkers', key: 'mediaEngelVolkers', width: 170, render: (v: number) => formatNumber(v) },
      { title: 'media_portales_total', dataIndex: 'mediaPortalesTotal', key: 'mediaPortalesTotal', width: 170, render: (v: number) => formatNumber(v) },
      { title: 'media_ventas_notaria', dataIndex: 'mediaVentasNotaria', key: 'mediaVentasNotaria', width: 180, render: (v: number) => formatNumber(v) },
      { title: 'media_ventas_swomey', dataIndex: 'mediaVentasSwomey', key: 'mediaVentasSwomey', width: 180, render: (v: number) => formatNumber(v) },
      { title: 'media_ventas_reales_total', dataIndex: 'mediaVentasRealesTotal', key: 'mediaVentasRealesTotal', width: 190, render: (v: number) => formatNumber(v) },
      { title: 'coef_antiguedad_ventas', dataIndex: 'coefAntiguedadVentas', key: 'coefAntiguedadVentas', width: 180, render: (v: number) => formatNumber(v) },
      { title: 'coef_historico', dataIndex: 'coefHistorico', key: 'coefHistorico', width: 140, render: (v: number) => formatNumber(v) },
      {
        title: 'motor',
        dataIndex: 'motorUsed',
        key: 'motorUsed',
        width: 110,
        render: (v?: 1 | 2 | null) => {
          if (v === 1) return <Tag color="green">Motor 1</Tag>;
          if (v === 2) return <Tag color="orange">Motor 2</Tag>;
          return <Tag>—</Tag>;
        },
      },
      {
        title: 'precio_m2_final',
        dataIndex: 'precioM2Final',
        key: 'precioM2Final',
        width: 140,
        render: (v: number) => (typeof v === 'number' ? `${v.toLocaleString('es-ES')} €/m²` : '-'),
      },
      {
        title: 'Acción rápida',
        key: 'quickAction',
        width: 220,
        fixed: 'right' as const,
        render: (_: any, row: AvmZone) => (
          <Space>
            <Form
              layout="inline"
              form={fotocasaForm}
              onFinish={async (values) => {
                if (savingFotocasaFor) return;
                const raw = values?.[`fotocasa-${row.zoneId}`];
                if (typeof raw !== 'number') return;
                try {
                  setSavingFotocasaFor(row.zoneId);
                  await avmApi.updateFotocasaAverage(row.zoneId, raw);
                  message.success(`media_fotocasa actualizada en ${row.zoneId}`);
                  fetchRows();
                } catch {
                  message.error('No se pudo actualizar media_fotocasa');
                } finally {
                  setSavingFotocasaFor(null);
                }
              }}
            >
              <Form.Item name={`fotocasa-${row.zoneId}`} style={{ marginBottom: 0 }}>
                <InputNumber placeholder="media_fotocasa" style={{ width: 130 }} min={0} />
              </Form.Item>
              <Button
                size="small"
                type="default"
                htmlType="submit"
                loading={savingFotocasaFor === row.zoneId}
              >
                Guardar
              </Button>
            </Form>
          </Space>
        ),
      },
    ],
    [fetchRows, fotocasaForm, savingFotocasaFor],
  );

  const handleImport = async () => {
    if (!importText.trim()) return;
    try {
      const { data } = await avmApi.importZonesFromText(importText);
      message.success(`Importadas ${data.imported} zonas. Omitidas: ${data.skipped}`);
      if (Array.isArray(data.errors) && data.errors.length) {
        message.warning(`Se detectaron ${data.errors.length} líneas con incidencias`);
      }
      setImportModalOpen(false);
      setImportText('');
      fetchRows();
    } catch {
      message.error('Error importando el listado. Revisa el formato.');
    }
  };

  const handleRunApify = async () => {
    try {
      const values = await runForm.validateFields();
      setRunningApify(true);
      const { data } = await avmApi.runFotocasaScraper(values);
      message.success(
        data?.runId
          ? `Run lanzado correctamente: ${data.runId}`
          : 'Run lanzado correctamente en Apify',
      );
      setRunModalOpen(false);
      runForm.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, 'No se pudo lanzar el run de Apify'));
    } finally {
      setRunningApify(false);
    }
  };

  const handleRunIdealista = async () => {
    try {
      const values = await runIdealistaForm.validateFields();
      setRunningIdealista(true);
      const { data } = await avmApi.runIdealistaScraper(values);
      message.success(
        data?.runId
          ? `Run Idealista lanzado: ${data.runId}`
          : 'Run Idealista lanzado correctamente',
      );
      setRunIdealistaModalOpen(false);
      runIdealistaForm.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, 'No se pudo lanzar el run de Idealista'));
    } finally {
      setRunningIdealista(false);
    }
  };

  const handleReprocessDataset = async () => {
    try {
      const values = await reprocessForm.validateFields();
      setReprocessingDataset(true);
      const { data } = await avmApi.reprocessDataset(values.datasetId, values.zoneId, values.source);
      message.success(
        `Dataset reprocesado (${data?.source ?? 'auto'}). Zonas actualizadas: ${data?.zonesUpdated ?? 0}, omitidas: ${data?.zonesSkipped ?? 0}`,
      );
      setReprocessModalOpen(false);
      reprocessForm.resetFields();
      fetchRows();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, 'No se pudo reprocesar el dataset'));
    } finally {
      setReprocessingDataset(false);
    }
  };

  const handleRecomputeSales = async () => {
    setRecomputingSales(true);
    try {
      const { data } = await avmApi.recomputeAllSalesAverages();
      message.success(
        `Medias de ventas recalculadas en ${data?.zonesRecomputed ?? 0} zonas`,
      );
      fetchRows();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'No se pudieron recalcular las medias de ventas'));
    } finally {
      setRecomputingSales(false);
    }
  };

  const handleRecomputeFinalPrices = async () => {
    setRecomputingFinalPrices(true);
    try {
      const { data } = await avmApi.recomputeAllFinalPrices();
      message.success(
        `Precio m² final recalculado en ${data?.zonesUpdated ?? 0} zonas (omitidas: ${data?.zonesSkipped ?? 0})`,
      );
      fetchRows();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'No se pudo recalcular el precio m² final'));
    } finally {
      setRecomputingFinalPrices(false);
    }
  };

  const moreActionsMenu = {
    items: [
      {
        key: 'recompute-sales',
        icon: <Sigma size={14} />,
        label: 'Recalcular medias de ventas',
        disabled: recomputingSales,
        onClick: handleRecomputeSales,
      },
      {
        key: 'recompute-final',
        icon: <Sigma size={14} />,
        label: 'Recalcular precio m² final',
        disabled: recomputingFinalPrices,
        onClick: handleRecomputeFinalPrices,
      },
      {
        key: 'reprocess',
        icon: <RotateCcw size={14} />,
        label: 'Reprocesar datasetId',
        onClick: () => setReprocessModalOpen(true),
      },
      {
        key: 'import',
        icon: <Upload size={14} />,
        label: 'Importar listado',
        onClick: () => setImportModalOpen(true),
      },
      {
        key: 'reload',
        icon: <RefreshCw size={14} />,
        label: 'Recargar',
        onClick: () => fetchRows(),
      },
    ],
  };

  return (
    <div className="avm-page">
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>Valorador AVM</Title>
          <Text type="secondary">Base de zonas, medias y coeficientes para el motor AVM.</Text>
        </div>

        {isMobile ? (
          <Space wrap style={{ width: '100%' }}>
            <Button
              type="primary"
              icon={<Play size={14} />}
              onClick={() => setRunModalOpen(true)}
              style={{ flex: 1 }}
            >
              Scraping Fotocasa
            </Button>
            <Button
              icon={<Play size={14} />}
              onClick={() => setRunIdealistaModalOpen(true)}
              style={{ flex: 1 }}
            >
              Scraping Idealista
            </Button>
            <Dropdown menu={moreActionsMenu} trigger={['click']}>
              <Button icon={<MoreHorizontal size={14} />}>Más acciones</Button>
            </Dropdown>
          </Space>
        ) : (
          <Space wrap style={{ justifyContent: 'flex-end', maxWidth: '100%' }}>
            <Button type="primary" icon={<Play size={14} />} onClick={() => setRunModalOpen(true)}>
              Lanzar scraping Fotocasa
            </Button>
            <Button icon={<Play size={14} />} onClick={() => setRunIdealistaModalOpen(true)}>
              Lanzar scraping Idealista
            </Button>
            <Button
              icon={<Sigma size={14} />}
              loading={recomputingSales}
              onClick={handleRecomputeSales}
            >
              Recalcular medias de ventas
            </Button>
            <Button
              icon={<Sigma size={14} />}
              loading={recomputingFinalPrices}
              onClick={handleRecomputeFinalPrices}
            >
              Recalcular precio m² final
            </Button>
            <Button icon={<RotateCcw size={14} />} onClick={() => setReprocessModalOpen(true)}>
              Reprocesar datasetId
            </Button>
            <Button icon={<Upload size={14} />} onClick={() => setImportModalOpen(true)}>
              Importar listado
            </Button>
            <Button icon={<RefreshCw size={14} />} onClick={fetchRows}>
              Recargar
            </Button>
          </Space>
        )}
      </div>

      <Tabs
        className="avm-tabs"
        size={isMobile ? 'small' : 'middle'}
        defaultActiveKey="zones"
        items={[
          {
            key: 'zones',
            label: isMobile ? 'Zonas' : 'Zonas AVM',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                    <Input.Search
                      allowClear
                      placeholder={isMobile ? 'Buscar zona…' : 'Buscar por ID_ZONA, municipio, zona o barrio'}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onSearch={() => fetchRows()}
                      style={{ width: isMobile ? '100%' : 420, maxWidth: '100%' }}
                    />
                    <Text type="secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Database size={14} /> {rows.length} zonas cargadas
                    </Text>
                  </Space>
                </Card>

                <Table
                  rowKey="zoneId"
                  loading={loading}
                  columns={columns as any}
                  dataSource={rows}
                  pagination={{ pageSize: isMobile ? 20 : 50, size: isMobile ? 'small' : undefined }}
                  scroll={{ x: 3010, y: isMobile ? 480 : 620 }}
                  size="small"
                />
              </>
            ),
          },
          {
            key: 'sales',
            label: isMobile ? 'Ventas' : 'Media de ventas',
            children: <AvmSalesTab />,
          },
          {
            key: 'fotocasa-listings',
            label: isMobile ? 'Fotocasa' : 'Testigos Fotocasa',
            children: <AvmFotocasaListingsTab />,
          },
          {
            key: 'idealista-listings',
            label: isMobile ? 'Idealista' : 'Testigos Idealista',
            children: <AvmIdealistaListingsTab />,
          },
          {
            key: 'calculations',
            label: isMobile ? 'Cálculos' : 'Cálculos (trazabilidad)',
            children: <AvmCalculationsTab />,
          },
          {
            key: 'sessions',
            label: isMobile ? 'Actividad' : 'Actividad valorador',
            children: <AvmSessionsTab />,
          },
          {
            key: 'valorador-emails',
            label: isMobile ? 'Emails' : 'Emails valorador (temp)',
            children: <AvmValoradorEmailsTab />,
          },
        ]}
      />

      <Modal
        title="Reprocesar dataset de Apify"
        open={reprocessModalOpen}
        onCancel={() => setReprocessModalOpen(false)}
        onOk={handleReprocessDataset}
        okText="Reprocesar"
        confirmLoading={reprocessingDataset}
        width={isMobile ? '100%' : 680}
      >
        <Text type="secondary">
          Recalcula AVM inmediatamente desde un dataset ya generado en Apify. No depende del webhook.
        </Text>
        <Divider style={{ margin: '12px 0' }} />
        <Form
          form={reprocessForm}
          layout="vertical"
        >
          <Form.Item
            label="Dataset ID"
            name="datasetId"
            rules={[{ required: true, message: 'Dataset ID obligatorio' }]}
          >
            <Input placeholder="Ej: a1B2c3D4e5F6" />
          </Form.Item>
          <Form.Item
            label="Zone ID (opcional)"
            name="zoneId"
            extra="Si lo informas, fuerza el reproceso para esa zona concreta (ej: ID-001)."
          >
            <Input placeholder="ID-001" />
          </Form.Item>
          <Form.Item
            label="Source (opcional)"
            name="source"
            extra="Si no se informa, se autodetecta del actor / items."
          >
            <Select
              allowClear
              placeholder="auto-detect"
              options={[
                { value: 'fotocasa', label: 'Fotocasa' },
                { value: 'idealista', label: 'Idealista' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Lanzar run Apify (Fotocasa)"
        open={runModalOpen}
        onCancel={() => setRunModalOpen(false)}
        onOk={handleRunApify}
        okText="Lanzar run"
        confirmLoading={runningApify}
        width={isMobile ? '100%' : 760}
      >
        <Text type="secondary">
          Este botón ejecuta el actor de Apify y, si el backend tiene configurado el webhook automático, el recalculo AVM se aplicará solo al completarse el run. El reproceso manual queda como fallback.
        </Text>
        <Divider style={{ margin: '12px 0' }} />
        <Form
          form={runForm}
          layout="vertical"
          initialValues={{
            maxItems: 40,
            location: 'Mallorca',
            operation: 'buy',
            propertyType: 'home',
            minPrice: 0,
            maxPrice: 0,
            minSize: 0,
            maxSize: 0,
            publicationDate: 'lastMonth',
            sortBy: 'rating',
          }}
        >
          <Space wrap>
            <Form.Item label="Location" name="location" rules={[{ required: true, message: 'Location obligatoria' }]}>
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item label="Operation" name="operation" rules={[{ required: true, message: 'Operation obligatoria' }]}>
              <Input style={{ width: 130 }} />
            </Form.Item>
            <Form.Item label="Property Type" name="propertyType" rules={[{ required: true, message: 'Property Type obligatorio' }]}>
              <Input style={{ width: 140 }} />
            </Form.Item>
            <Form.Item label="Max Items" name="maxItems" rules={[{ required: true, message: 'Max Items obligatorio' }]}>
              <InputNumber min={1} max={200} style={{ width: 120 }} />
            </Form.Item>
          </Space>

          <Space wrap>
            <Form.Item label="Min Price" name="minPrice">
              <InputNumber min={0} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="Max Price" name="maxPrice">
              <InputNumber min={0} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="Min Size" name="minSize">
              <InputNumber min={0} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="Max Size" name="maxSize">
              <InputNumber min={0} style={{ width: 120 }} />
            </Form.Item>
          </Space>

          <Space wrap>
            <Form.Item label="Publication Date" name="publicationDate">
              <Select style={{ width: 180 }} options={[
                { value: '', label: 'Sin filtro' },
                { value: 'last48h', label: 'Últimas 48h' },
                { value: 'lastWeek', label: 'Última semana' },
                { value: 'lastMonth', label: 'Último mes' },
              ]} />
            </Form.Item>
            <Form.Item label="Sort By" name="sortBy">
              <Input style={{ width: 180 }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="Lanzar run Apify (Idealista)"
        open={runIdealistaModalOpen}
        onCancel={() => setRunIdealistaModalOpen(false)}
        onOk={handleRunIdealista}
        okText="Lanzar run"
        confirmLoading={runningIdealista}
        width={isMobile ? '100%' : 680}
      >
        <Text type="secondary">
          Lanza el actor Smart Idealista Scraper. Si está configurado el webhook automático,
          el procesamiento AVM (persistencia de testigos + cálculo de mediaIdealista)
          se aplicará al completarse el run. Si no, usa "Reprocesar datasetId".
        </Text>
        <Divider style={{ margin: '12px 0' }} />
        <Form
          form={runIdealistaForm}
          layout="vertical"
          initialValues={{
            maxItems: 1000,
            locationId: '0-EU-ES-07-01',
            locationName: 'Mallorca',
            operation: 'sale',
            language: 'es',
            order: 'mostrecent',
          }}
        >
          <Space wrap>
            <Form.Item label="Location ID" name="locationId" rules={[{ required: true }]}>
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item label="Location Name" name="locationName">
              <Input style={{ width: 200 }} />
            </Form.Item>
            <Form.Item label="Operation" name="operation">
              <Select
                style={{ width: 130 }}
                options={[
                  { value: 'sale', label: 'Sale' },
                  { value: 'rent', label: 'Rent' },
                ]}
              />
            </Form.Item>
          </Space>
          <Space wrap>
            <Form.Item label="Max Items" name="maxItems" rules={[{ required: true }]}>
              <InputNumber min={1} max={5000} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item label="Language" name="language">
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="Order" name="order">
              <Select
                style={{ width: 180 }}
                options={[
                  { value: 'mostrecent', label: 'Más recientes' },
                  { value: 'price', label: 'Precio asc' },
                  { value: 'priceDown', label: 'Precio desc' },
                ]}
              />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="Importar listado AVM"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={handleImport}
        okText="Importar"
        width={isMobile ? '100%' : 880}
      >
        <Text type="secondary">
          Pega líneas con formato tipo: ID-001 Mallorca Centro Cort Alto. También admite cabecera.
        </Text>
        <Input.TextArea
          rows={16}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder="ID_ZONA ISLA MUNICIPIO ZONA BARRIO CLASIFICACION_MICROZONA ..."
          style={{ marginTop: 10 }}
        />
      </Modal>
    </div>
  );
}
